import React from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "../i18n/I18nContext";
import { GRADEBOOK_STATUS_BADGE, GRADEBOOK_STATUS_LABEL_KEY } from "../utils/gradebookStatus";
import { levelKey } from "../utils/classesLabels";

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
};

// Deep-linked from ListaNotasLayer's "Visualizar" action (both the desktop
// table row and the mobile card) — same idea as PdfPreviewLayer: a single
// focused card (back button + content), with none of the list screen's
// filters/other cards around it, so it reads cleanly full-screen on a phone.
// The gradebook + its precomputed period label/graded stats arrive via
// location.state instead of being refetched, since ListaNotasLayer already
// has them in hand — a page refresh loses that state, hence the fallback
// below (same tradeoff PdfPreviewLayer/LancamentoNotasLayer's editGradebook
// deep-link already accept).
const PautaVisualizarLayer = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { gradebook: gb, periodLabel, gradedStats } = location.state || {};
  const goBack = () => navigate(-1);

  if (!gb) {
    return (
      <div className='card'>
        <div className='card-body text-center py-64'>
          <p className='text-secondary-light mb-16'>{t("nota.view.notFound")}</p>
          <button type='button' className='btn btn-primary-600' onClick={goBack}>
            <i className='ri-arrow-left-line' /> {t("global.back")}
          </button>
        </div>
      </div>
    );
  }

  const studentGrades = (gb.grades || [])
    .slice()
    .sort((a, b) => (a.student?.name || "").localeCompare(b.student?.name || "", "pt"));
  const { graded, total, percent } = gradedStats || { graded: 0, total: studentGrades.length, percent: 0 };

  const details = [
    { label: t("turma.col.className"), value: gb.classroom?.name },
    { label: t("disciplina.field.subjectName"), value: gb.subject?.name },
    { label: t("nota.field.evaluation"), value: gb.evaluation?.name },
    { label: t("nota.filter.period"), value: periodLabel },
    { label: t(levelKey("turma.field.educationLevel")), value: gb.level?.description },
    { label: t("class.evaluationDate"), value: formatDate(gb.evaluationDate) },
    { label: t("nota.col.teacher"), value: gb.teacher?.name },
    { label: t("nota.detail.degree"), value: gb.degree },
    { label: t(levelKey("nota.detail.degreeLevel")), value: gb.degreeLevel },
    { label: t("nota.detail.teacherNotes"), value: gb.teacherNotes },
    { label: t("nota.detail.notes"), value: gb.notes },
  ].filter((item) => item.value);

  return (
    <div className='card'>
      <div className='card-header'>
        <div className='d-flex flex-wrap align-items-center justify-content-between gap-2'>
          <div className='d-flex align-items-center gap-2' style={{ minWidth: 0 }}>
            <button
              type='button'
              className='btn btn-sm btn-outline-secondary radius-8 d-inline-flex align-items-center gap-1 flex-shrink-0'
              onClick={goBack}
            >
              <Icon icon='solar:arrow-left-linear' className='text-xl' />
              {t("global.back")}
            </button>
            <h6 className='mb-0 text-md text-truncate'>
              {gb.subject?.name} — {gb.evaluation?.name}
            </h6>
          </div>
          <span className={`badge flex-shrink-0 ${GRADEBOOK_STATUS_BADGE[gb.status] || "bg-neutral-200 text-neutral-600"}`}>
            {t(GRADEBOOK_STATUS_LABEL_KEY[gb.status]) || gb.status}
          </span>
        </div>
      </div>
      <div className='card-body'>
        <div className='row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-3 mb-24'>
          {details.map((item) => (
            <div key={item.label}>
              <span className='text-secondary-light text-xs d-block'>{item.label}</span>
              <span className='fw-medium'>{item.value}</span>
            </div>
          ))}
        </div>

        <div className='d-flex align-items-center gap-8 mb-16'>
          <div
            className='progress bg-primary-50 rounded-pill h-8-px'
            style={{ width: 120 }}
            role='progressbar'
            aria-valuenow={graded}
            aria-valuemin={0}
            aria-valuemax={total}
          >
            <div className='progress-bar bg-primary-600 rounded-pill' style={{ width: `${percent}%` }} />
          </div>
          <span className='text-secondary-light text-sm text-nowrap'>{t("nota.hint.gradedCount", { graded, total })}</span>
        </div>

        {studentGrades.length === 0 ? (
          <div className='alert alert-info mb-0'>{t("nota.hint.noStudentsGraded")}</div>
        ) : (
          <div className='table-responsive'>
            <table className='table bordered-table mb-0'>
              <thead>
                <tr>
                  <th>{t("presenca.col.name")}</th>
                  <th>{t("nota.col.grade")}</th>
                </tr>
              </thead>
              <tbody>
                {studentGrades.map((g, idx) => {
                  const hasGrade = g.grade !== null && g.grade !== undefined && g.grade !== "";
                  const passed = hasGrade && Number(g.grade) >= 10;
                  return (
                    <tr key={g.student?.id || idx}>
                      <td className='fw-medium'>{g.student?.name || "-"}</td>
                      <td>
                        <span className={hasGrade ? (passed ? "text-success-main fw-semibold" : "text-danger-main fw-semibold") : "text-secondary-light"}>
                          {hasGrade ? g.grade : "-"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PautaVisualizarLayer;
